import React from "react";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Shield,
  Scale,
  Gavel,
  PenTool,
  MessageSquare,
  Clock,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

export default function GreatLawyersThinkArticle() {
  return (
    <div className="bg-white dark:bg-[#0B1412] min-h-screen flex flex-col transition-colors duration-500">
      <Head>
        <title>
          What Makes a Great Lawyer: The Habits of Legal Thinking | Durmah
        </title>
        <meta
          name="description"
          content="Explore the fundamental habits of legal reasoning, precision, and ethics. An academic guide on how great lawyers think and the discipline of legal practice."
        />
        <link key="route-canonical"
          rel="canonical"
          href="https://www.durmah.ai/insights/how-great-lawyers-think"
        />
      </Head>

      <main className="flex-1">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <Link
            href="/#core-pillars"
            className="inline-flex items-center gap-2 text-sm text-[#123733] dark:text-[#D5BF76] font-black mb-8 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Explore the Durmah pillars
          </Link>

          <div className="prose prose-indigo dark:prose-invert max-w-none">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D5BF76]/20 text-[#D5BF76] text-[10px] font-black uppercase tracking-widest border border-[#D5BF76]/30 mb-6 font-sans">
              <Scale className="w-3.5 h-3.5" />
              <span>Academic Insight</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white mb-6 tracking-tight leading-tight font-sans">
              What Makes a Great Lawyer: <br />
              <span className="text-[#D5BF76]">
                The Habits of Legal Thinking
              </span>
            </h1>

            <p className="text-xl text-gray-600 dark:text-teal-100/70 leading-relaxed mb-12 font-medium">
              Legal mastery is not merely the accumulation of statutes and
              precedents. It is a specific cognitive orientation: a discipline of
              thought that prioritises precision, scepticism, and ethical
              responsibility.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              1. Public Trust & The Ethical Foundation
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              The legal profession is a public trust. A great
              lawyer understands that their primary duty is to the court and the
              administration of justice. This requires a level of honesty that
              extends beyond mere compliance; it demands intellectual integrity.
              Whether advising a client or presenting an argument, the lawyer
              must remain anchored in the truth, recognising that their
              reputation for honesty is their most valuable asset.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              2. Structured Legal Reasoning
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              Legal reasoning is a formal process. It requires the ability to
              identify the relevant legal issues, locate the governing rules,
              apply those rules to the facts, and reach a reasoned conclusion. A
              great lawyer does not just look for evidence that supports their
              position; they actively seek out counter-arguments and acknowledge
              uncertainty. Weighing conflicting principles against each other is
              what distinguishes a practitioner from a scholar.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              3. Precision in Language
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              For a lawyer, language is the primary instrument of their trade.
              Precision is not an aesthetic choice; it is a functional
              necessity. This involves a rigorous attention to definitions, the
              careful distinction between similar concepts, and the avoidance of
              ambiguity. A great lawyer understands that a misplaced comma or a
              poorly defined term can have significant legal consequences.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              4. Intellectual Honesty & Verification
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              The habit of verification is the hallmark of professional
              competence. A great lawyer never takes a source at face value.
              They verify primary materials, check citations, and ensure that
              their understanding of the law is current. Intellectual honesty
              requires the admission of when a legal point is weak or when a
              precedent is not directly applicable.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              5. Judgment Under Uncertainty
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              Law often operates in the "grey zones." A great lawyer develops
              the discretion to make sound judgments even when the law is
              unclear or the facts are disputed. This involves weighing risks,
              anticipating potential outcomes, and providing balanced advice
              that accounts for the inherent unpredictability of the legal
              process.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              6. Clarity in Writing & Oral Reasoning
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              Whether drafting an essay, a memo, or a skeleton argument, clarity
              is paramount. This requires clear signposting and effective
              structure, often using frameworks like IRAC (Issue, Rule,
              Application, Conclusion). In oral reasoning, a great lawyer speaks with
              restraint and clarity, building confidence in seminars or vivas by
              focusing on logical progression rather than rhetorical flourish.
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
              7. Professional Habits: Discipline & Consistency
            </h2>
            <p className="text-lg leading-relaxed mb-12">
              Success in the law is as much about character as it is about
              intellect. Professional habits form the bedrock of a successful
              career: meticulous preparation, time discipline from Michaelmas to
              June, and consistent revision routines.
            </p>

            {/* Durmah SUPPORT SECTION */}
            <div className="not-prose my-16 p-8 md:p-12 bg-[#123733] rounded-[2.5rem] border border-[#D5BF76]/30 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D5BF76]/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 text-[#D5BF76] mb-6 font-sans">
                  <Shield className="w-8 h-8" />
                  <span className="text-xs font-black uppercase tracking-[0.2em]">
                    The Durmah Methodology
                  </span>
                </div>

                <h2 className="text-3xl md:text-4xl font-black text-white mb-8 tracking-tight font-sans">
                  How Durmah Supports These Habits
                </h2>

                <div className="space-y-6 text-teal-50/90 text-lg leading-relaxed">
                  <p>
                    Durmah is designed not to replace the student's intellect,
                    but to provide the infrastructure necessary to build these
                    habits of legal thinking.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                    <div className="bg-white/5 p-5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-2 text-[#D5BF76] mb-2">
                        <BookOpen className="w-4 h-4" />
                        <span className="font-bold text-sm uppercase tracking-wider">
                          Lexicon
                        </span>
                      </div>
                      <p className="text-sm opacity-80">
                        Refining precision in language through deep-dive
                        contextual terminology.
                      </p>
                    </div>
                    <div className="bg-white/5 p-5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-2 text-[#D5BF76] mb-2">
                        <MessageSquare className="w-4 h-4" />
                        <span className="font-bold text-sm uppercase tracking-wider">
                          Oral Reasoning
                        </span>
                      </div>
                      <p className="text-sm opacity-80">
                        Speak/Quiz practice to build confidence in oral
                        reasoning and viva preparation.
                      </p>
                    </div>
                    <div className="bg-white/5 p-5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-2 text-[#D5BF76] mb-2">
                        <PenTool className="w-4 h-4" />
                        <span className="font-bold text-sm uppercase tracking-wider">
                          Structured Notes
                        </span>
                      </div>
                      <p className="text-sm opacity-80">
                        Transforming raw lecture data into organised, structured
                        legal notes.
                      </p>
                    </div>
                    <div className="bg-white/5 p-5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-2 text-[#D5BF76] mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-bold text-sm uppercase tracking-wider">
                          Planning
                        </span>
                      </div>
                      <p className="text-sm opacity-80">
                        Aligning study routines with your own module deadlines
                        to build professional discipline.
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-[#D5BF76]/80 pt-4 leading-relaxed">
                    <span className="font-semibold">Durmah principle:</span>{" "}
                    Legal judgment develops through the consistent practice of
                    intellectual and ethical habits. Durmah is designed to
                    support that practice, not replace it.
                  </p>
                </div>
              </div>
            </div>

            <div className="py-12 border-t border-gray-100 dark:border-white/5">
              <Link href="/login" className="group block">
                <div className="flex items-center justify-between p-8 rounded-3xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-all border border-transparent hover:border-[#D5BF76]/20">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#D5BF76] mb-2 block">
                      Get Started
                    </span>
                    <h4 className="text-2xl font-black text-gray-900 dark:text-white group-hover:text-[#D5BF76] transition-colors">
                      Try Durmah for free
                    </h4>
                  </div>
                  <ArrowRight className="w-8 h-8 text-gray-300 group-hover:text-[#D5BF76] transition-all transform group-hover:translate-x-2" />
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
